import React from 'react';

const guides: Record<string, { title: string; description: string; color: string; tips?: string[] }> = {
  zerolag_macd: {
    title: 'ZeroLag MACD (DEMA 방식)',
    description: '이중 지수이평선(DEMA) 개념을 도입하여 MACD 고유의 단점인 후행 지연을 0에 가깝게 당긴 최첨단 추세 전략입니다.',
    color: 'primary',
    tips: [
      '봉 설정: 1시간봉(1h) 추천 (30m은 휩쏘가 잦아 수수료 부담 가중)',
      'EMA 120/240 저항선 익절: 켜는 것을 적극 권장합니다. 하위 봉인 30분봉의 흐름 꺾임을 보고 영리하게 조기 익절합니다.',
      'K선 각도 필터: K선이 확연히 고개를 들 때만 진입하여 데드캣 반등 휩쏘를 방지합니다.',
    ],
  },
  channel_trend2: {
    title: 'Channel Trend V2 (EMA Angle)',
    description: '선형 회귀 채널(LR)의 경계 돌파와 단기 EMA의 크로스 각도를 결합해 횡보장 끝자락 및 강한 반등 시점을 노립니다.',
    color: 'purple',
    tips: [
      '경계선 영역(Boundary): 20%~40% 영역 설정 시 이니시 스타일 극대화',
      '각도 임계치: -1.0 ~ 0.0 사이로 설정하여 휩쏘 차단',
    ],
  },
  kewma: {
    title: 'KEWMA (Kalman EMA)',
    description: '칼만 필터와 지수이평(EWMA)을 융합하여 미세 잡음을 감쇠하고 순수 트렌드 방향성을 정교하게 계산해 추세 추종합니다.',
    color: 'blue',
  },
  stochastic_ema: {
    title: 'Stochastic EMA',
    description: 'EMA 10/20/60 정배열·역배열 거시 추세 필터 + Slow/Fast Stochastic 골든/데드 크로스 진입 타점 + ADX 횡보장 차단. ATR 기반 TP/SL로 변동성에 대응하며, 1시간봉에서 ETH +1.44% (100% 승률) 기록.',
    color: 'cyan',
    tips: [
      '추천 간격: 1시간봉(1h) — 거래 횟수는 적지만 높은 승률',
      'disable_indicator_exit=true: 반대 크로스 청산 비활성화 필수 (ATR TP/SL에 위임)',
      '추천 종목: ETH, BTC, SOL, ENA, TURBO 등 추세형 코인',
      '주의: 급등 구간 알트코인(NEAR 등) 숏 진입 시 대손절 위험',
    ],
  },
  auto_signal: {
    title: 'CenIchi 4Ema Pullback (Auto1)',
    description: 'EMA 장기 추세 + Ichimoku Cloud 구름대 돌파 + RSI(5) 과매도/과매수 기반 다중 필터 신호 전략. 단순하면서도 강력한 수익률(15m +13.68%, 1h +18.43%)을 기록한 ETH 전용 전략입니다.',
    color: 'amber',
    tips: [
      '추천 간격: 15분봉(15m) 또는 1시간봉(1h) — 두 TF 모두에서 검증된 성과',
      '추천 종목: ETHUSDT 전용으로 검증됨',
    ],
  },
  perfect_alignment_pullback: {
    title: 'Perfect Alignment Pullback',
    description: 'EMA 60/89/120 정배열/역배열에 터치할 때의 눌림목 타점을 공략하고, EMA 20/60 크로스오버로 깔끔하게 탈출하는 추세 추종 전략입니다. EMA 240 매크로 추세 필터로 상승장/하락장을 구분하여 휩쏘를 원천 방어합니다.',
    color: 'purple',
    tips: [
      '추천 종목: BTC, ETH 등 메이저 코인 및 거래량 상위 알트코인',
      '매크로 추세 필터 사용: 횡보장이 아닌 강한 상승장/하락장에서는 필터를 켜서 역추세 진입을 막으십시오.',
    ],
  },
};

const colorMap: Record<string, { bg: string; border: string; text: string }> = {
  primary: { bg: 'bg-primary/10', border: 'border-primary/20', text: 'text-primary' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
};

interface Props {
  strategy_type: string;
}

export const SignalSidePanelGuide: React.FC<Props> = ({ strategy_type }) => {
  const guide = guides[strategy_type];
  const knownStrategies = Object.keys(guides);

  if (!knownStrategies.includes(strategy_type)) {
    return (
      <div className="p-4 bg-muted border border-border rounded-xl text-center text-muted-foreground text-[11px]">
        현재 선택된 전략의 특수 가이드가 준비되지 않았습니다. <br />
        설정 폼 내 도메인 지표 설명을 참고해 주세요.
      </div>
    );
  }

  const colors = colorMap[guide.color] || colorMap.primary;

  return (
    <div className="space-y-3">
      <div className={'p-3 rounded-xl border ' + colors.bg + ' ' + colors.border}>
        <h4 className={'font-black text-sm mb-1 ' + colors.text}>{guide.title}</h4>
        <p className="text-[11px] text-muted-foreground">{guide.description}</p>
      </div>
      {guide.tips && (
        <div className="space-y-1.5 p-3 border border-border rounded-xl">
          <p className="font-bold text-muted-foreground">💡 최적 세팅 팁:</p>
          <ul className="list-disc pl-4 space-y-1 text-muted-foreground text-[11px]">
            {guide.tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
